import { useEffect, useMemo, useState } from "react";
import { TIPS } from "../../src/web/types.ts";
import { ApiError, api, type Me, type Person, type SharedView, type TripView, termsAsked } from "./api.ts";
import { BeforeWeStart } from "./BeforeWeStart.tsx";
import { early } from "./early.ts";
import { failureOf } from "./failure.ts";
import { useLoad } from "./hooks.ts";
import { NewVersion } from "./NewVersion.tsx";
import { useAgain } from "./offline.ts";
import { Failed, Opening, settle } from "./opening.tsx";
import type { Route } from "./router.ts";
import { Button } from "./souvenir.ts";
import { ViewedTrip } from "./TripPage.tsx";
import { ButtonLink, Message } from "./ui.tsx";
import { connectUrl } from "./ViewBar.tsx";
import { Viewing, type ViewingInfo } from "./viewing.ts";

// A shared trip's view link, /v/<code> (features/sharing.md): the trip's own views, read-only, for anyone it's shared
// with, from the view's own API only (api.view: never the members' /api/trips, never who I am). Signed out for a trip
// shared with anyone; for a list, signing in (with Google) first, then the screen of 16-or-older and the Terms, as for
// anyone signing up. Never indexed: the page says so, as its answer's header does.

export const viewKey = (code: string) => `view:${code}`;

// A face with nothing on it: Souvenir's avatar draws a photo over the person's colour, so an empty one leaves the
// colour alone, never initials ("A traveller" isn't anyone's name).
const NO_FACE = "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%2F%3E";

export function ViewPage({ route }: { route: Extract<Route, { name: "view" }> }) {
  const { code } = route;
  // Bumped after the Terms are agreed to, or Try again: load it again.
  const [version, setVersion] = useState(0);
  const again = useAgain();
  const data = useLoad(() => early(viewKey(code), () => api.view(code)), [code, version, again]);
  // Each day's weather, on its own: the plan never waits for it.
  const weather = useLoad(() => api.viewWeather(code), [code, version, again]);
  useEffect(settle, []);
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.append(robots);
    return () => robots.remove();
  }, []);
  const view = data.state === "ready" ? data.data : null;
  const shown = useMemo(() => (view ? asTrip(view) : null), [view]);
  if (data.state === "loading")
    return (
      <div className="tm-trip-page" aria-busy="true">
        <Opening />
      </div>
    );
  if (data.state === "failed") {
    const asked = termsAsked(data.error);
    if (asked)
      return (
        <>
          <BeforeWeStart
            key={asked.version}
            asked={asked}
            onAgreed={async () => setVersion((v) => v + 1)}
            onChanged={() => setVersion((v) => v + 1)}
          />
          <NewVersion floating />
        </>
      );
    const refused = refusedWords(data.error, code);
    if (refused) return <Message title={refused.title} body={refused.body} action={refused.action} />;
    const failure = failureOf(data.error);
    return (
      <Failed
        failure={failure ?? "not-answering"}
        what="trip"
        again={() => setVersion((v) => v + 1)}
        retrying={data.retrying === true}
      />
    );
  }
  if (!view || !shown) return null;
  return (
    <Viewing.Provider value={shown.viewing}>
      <ViewedTrip
        me={shown.me}
        trip={shown.trip}
        shared={view}
        weather={weather.state === "ready" ? weather.data : null}
        route={route}
        onChanged={() => setVersion((v) => v + 1)}
      />
    </Viewing.Provider>
  );
}

// The trip as the trip's views take it, from what the view's API gave: nobody is "me", nothing waits on anyone, no
// join link, locations, invitation or spots, and Money only with its switch.
function asTrip(view: SharedView): { trip: TripView; me: Me; viewing: ViewingInfo } {
  const t = view.trip;
  const money = t.money ?? {
    paid: [],
    coming_up: [],
    refunded: [],
    refunds_due: [],
    booked_unpaid: [],
    by_category: { stays: [], transport: [], food: [], activities: [], other: [] },
    charges: [],
  };
  // Someone whose name and photo aren't shown is a face in their colour only (a photo of nothing on it).
  const faced = (p: Person): Person => (p.anonymous ? { ...p, src: NO_FACE } : p);
  const trip: TripView = {
    id: t.code,
    title: t.title,
    dates: t.dates,
    version: 0,
    owner: t.owner,
    you: "",
    members: t.members.map(faced),
    requests: [],
    former: t.former,
    join_link: "",
    last_change: 0,
    location_window: { open: false },
    locations: [],
    invite: null,
    locked: null,
    money: { ...money, budget: t.budget, estimate: null },
    your_currency: null,
  };
  const me: Me = {
    user: { id: "viewer", name: "", email: "", photo: null, color: null },
    mcp_url: "",
    map: t.map,
    ais: [],
    tips_seen: [...TIPS],
    time_zone: null,
    currency: null,
  };
  const viewing: ViewingInfo = {
    code: t.code,
    sharedBy: t.shared_by,
    sample: t.sample ? { people: t.members.length } : null,
    shares: new Set(t.shares),
    who: t.who,
    viewer: t.viewer,
  };
  return { trip, me, viewing };
}

// Why the link didn't open, in plain words, with the one thing to do.
function refusedWords(error: Error, code: string): { title: string; body: string; action?: React.ReactNode } | null {
  if (!(error instanceof ApiError)) return null;
  const back = `/v/${encodeURIComponent(code)}`;
  const signIn = `/sign-in?next=${encodeURIComponent(back)}`;
  if (error.status === 401)
    return {
      title: "Sign in to see this trip",
      body: error.message,
      action: (
        <ButtonLink href={signIn} variant="primary">
          Sign in
        </ButtonLink>
      ),
    };
  if (error.status === 403)
    return {
      title: "This trip isn't shared with you",
      body: error.message,
      action: (
        <Button variant="secondary" onClick={() => api.signOut(signIn)}>
          Use another account
        </Button>
      ),
    };
  if (error.status === 404)
    return {
      title: "This link doesn't open a trip",
      body: error.message,
      action: (
        <ButtonLink href={connectUrl("shared")} variant="secondary">
          Plan your own trip with Tripmo
        </ButtonLink>
      ),
    };
  if (error.status === 429) return { title: "Opened a lot from here", body: error.message };
  return null;
}
